import { Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ContratoService, LicitacaoService } from 'administrativo-lib';
import {
  BaseResourceFormComponent, Contrato, ContratoAditamento, ContratoAnexo, ContratoHistorico,
  ContratoReajuste, ContratoResponsavel, DateFormatPipe, EddyAutoComplete,
  Favorecido, FavorecidoService, FuncaoService, GlobalService, Licitacao, LoginContabil,
  Prazo, TipoContratacao, TipoContrato
} from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { PrazoService } from '../../prazo/service/prazo.service';
import { TipoContratacaoService } from '../../tipo-contratacao/service/tipo-contratacao.service';
import { ContratoAditamentoService } from '../service/contrato-aditamento.service';
import { ContratoAnexoService } from '../service/contrato-anexo.service';
import { ContratoHistoricoService } from '../service/contrato-historico.service';
import { ContratoReajusteService } from '../service/contrato-reajuste.service';
import { ContratoResponsavelService } from '../service/contrato-responsavel.service';

declare var $: any;

@Component({
  selector: 'app-contrato-form',
  templateUrl: './contrato-form.component.html'
})
export class ContratoFormComponent extends BaseResourceFormComponent<Contrato, LoginContabil> implements OnInit {

  /**
   * Declaração de variáveis
   */
  @ViewChild('numero_') inputField: ElementRef;

  public listaAnexos = new Array<ContratoAnexo>();
  public uploadedFiles: any[] = [];
  public descricao: string = null;
  public dataContabil: Date = null;
  public displayHistorico: boolean = false;
  public cleanScreen: boolean = false;

  public tipos: { id: 'CONTRATO' | 'REGISTRO_PRECO', nome: string }[];
  public listaTipos: { id: TipoContrato, nome: string }[];
  public listaContratacao: TipoContratacao[];
  public listaPrazos: Prazo[];
  public listaResponsaveis: ContratoResponsavel[] = [];
  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;
  public licitacaoAutoComplete: EddyAutoComplete<Licitacao>;
  public listaAditamentos: Array<ContratoAditamento> = new Array<any>();
  public listaHistoricos: Array<ContratoHistorico> = new Array<any>();
  public listaReajustes: Array<ContratoReajuste> = new Array<any>();

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    private messageService: MessageService,
    protected injector: Injector,
    public funcaoService: FuncaoService,
    protected anexoService: ContratoAnexoService,
    protected aditamentoService: ContratoAditamentoService,
    protected historicoService: ContratoHistoricoService,
    protected reajusteService: ContratoReajusteService,
    protected globalService: GlobalService,
    protected prazoService: PrazoService,
    protected contratacaoService: TipoContratacaoService,
    protected contratoService: ContratoService,
    protected responsavelService: ContratoResponsavelService,
    protected licitacaoService: LicitacaoService,
    protected favorecidoService: FavorecidoService) {
    super(new Contrato(), injector, Contrato.converteJson, contratoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      ano: [null, [Validators.required]],
      data_assinatura: [null, [Validators.required]],
      data_inicio: [null, [Validators.required]],
      data_termino: [null, [Validators.required]],
      data_contabilizacao: [null, [Validators.required]],
      processo: [null, [Validators.required]],
      tipo_contrato: [null, [Validators.required]],
      tipo: ['CONTRATO', [Validators.required]],
      objeto: [null, [Validators.required]],
      aditivo: [null],
      gestor_cpf: [null],
      gestor_exige: [false],
      gestor: [null],
      gestor_cargo: [null],
      valor_contrato: [0, [Validators.required]],
      valor_garantia: [0],
      valor_caucao: [0],
      valor_material: [null],
      valor_servico: [null],
      enviar_email: [false, [Validators.required]],
      parente: [null],
      licitacao: [null],
      orgao: [this.login.orgao, [Validators.required]],
      favorecido: [null, [Validators.required]],
      prazo: [null, [Validators.required]],
      tipo_contratacao: [null, [Validators.required]],
      itens: [null],
      responsaveis: [null],
      data_historico: [null]
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'favorecido,favorecido.tipo,orgao,orgao.cidade,prazo,tipo_contratacao,licitacao.modalidade,licitacao_audesp' };
  }

  protected afterInit(): void {
    this.tipos = [{ id: 'CONTRATO', nome: 'Contrato' }, { id: 'REGISTRO_PRECO', nome: 'Registro de Preço' }];
    this.listaTipos = this.globalService.obterTiposContrato();
    this.contratacaoService.filtrar(0, 0, { orgao_id: this.login.orgao.id }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaContratacao = res ? res.content : [];
      }, error => toastr.error(error.message ? error.message : error));
    this.prazoService.filtrar(0, 0, { orgao_id: this.login.orgao.id }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaPrazos = res ? res.content : [];
      }, error => toastr.error(error.message ? error.message : error));

    // autocomplete para favorecido
    this.favorecidoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('favorecido'), this.favorecidoService,
      'id', ['cpf_cnpj', 'nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] }
    );
    this.licitacaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('licitacao'), this.licitacaoService,
      'numero', ['numero', 'modalidade.nome'], { orgao_id: this.login.orgao.id, orderBy: 'numero', relations: 'modalidade' }, { text: ['numero'] }
    );
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  protected afterLoad() {
    this.entidade.data_assinatura = new DateFormatPipe().transform(this.entidade.data_assinatura, []);
    this.entidade.data_inicio = new DateFormatPipe().transform(this.entidade.data_inicio, []);
    this.entidade.data_termino = new DateFormatPipe().transform(this.entidade.data_termino, []);
    this.entidade.data_contabilizacao = new DateFormatPipe().transform(this.entidade.data_contabilizacao, []);
    this.favorecidoAutoComplete.id = this.entidade.favorecido ? this.entidade.favorecido.id : null;
    this.licitacaoAutoComplete.id = this.entidade.licitacao ? this.entidade.licitacao.id : null;
    this.preencherTabelas();
  }

  public preencherTabelas() {
    this.anexoService.filtrar(1, -1, { contrato_id: this.entidade.id })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaAnexos = res.content;
      });

    this.aditamentoService.filtrar(0, -1, {
      'contrato.id': this.entidade.id,
      relations: 'contrato,contrato.tipo_contratacao,contrato.orgao,contrato.orgao.cidade,exercicio'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (data: any) => {
          this.listaAditamentos = data ? data.content : new Array<ContratoAditamento>();
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));

    this.responsavelService.filtrar(1, -1,
      { relations: 'responsavel', 'contrato.id': this.entidade.id, orderBy: 'id$DESC' })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaResponsaveis = res ? res.content : new Array<ContratoResponsavel>();
      }, error => toastr.warning(error.error.payload));

    this.historicoService.filtrar(0, -1, {
      'contrato.id': this.entidade.id,
      relations: 'contrato,tipo_contratacao,licitacao'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (data: any) => {
          this.listaHistoricos = data ? data.content : new Array<ContratoHistorico>();
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));

    this.reajusteService.filtrar(0, -1, {
      'contrato.id': this.entidade.id,
      relations: 'contrato'
    }).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (data: any) => {
          this.listaReajustes = data ? data.content : new Array<ContratoReajuste>();
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
  }

  protected async beforeSubmit() {
    try {
      if (this.entidadeForm.get('favorecido').value === null) {
        throw new Error('É necessário informar o nome do favorecido');
      }
      if (this.entidadeForm.get('data_assinatura').value === null) {
        throw new Error('É necessário informar a data de assinatura do contrato');
      }
      if (this.entidadeForm.get('data_inicio').value === null) {
        throw new Error('É necessário informar a data de início do contrato');
      }
      if (this.entidadeForm.get('data_contabilizacao').value === null) {
        throw new Error('É necessário informar a data de contabilização do contrato');
      }
      if (this.entidadeForm.get('data_termino').value === null) {
        throw new Error('É necessário informar a data de término do contrato');
      }
      if (this.entidadeForm.get('ano').value === null) {
        throw new Error('É necessário informar o ano do contrato');
      }
      if (this.entidadeForm.get('processo').value === null) {
        throw new Error('É necessário informar o número do processo');
      }
      if (this.entidadeForm.get('tipo_contratacao').value === null) {
        throw new Error('É necessário informar o tipo de contratação');
      }
      if (this.entidadeForm.get('tipo_contrato').value === null) {
        throw new Error('É necessário informar o tipo de contrato');
      }
      if (this.entidadeForm.get('objeto').value === null) {
        throw new Error('É necessário informar o objeto do contrato');
      }
      const dtContabilizacao: Date = this.entidadeForm.get('data_contabilizacao').value;
      if (dtContabilizacao.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data de contabilização está diferente do exercício logado');
      }
      this.entidadeForm.get('responsaveis').setValue(this.listaResponsaveis);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  /*
  * Faz a verificação de campos que fazem parte do envio para o AUDESP.
  * Se houve alteração desses campos ele grava no histórico para posterior envio.
  */
  public async verificarMudancaAudesp(limpa?: boolean) {
    if (this.entidade.id && (this.entidade.processo !== this.entidadeForm.get('processo').value ||
      this.entidade.data_inicio !== this.entidadeForm.get('data_inicio').value ||
      this.entidade.data_termino !== this.entidadeForm.get('data_termino').value || this.entidade.tipo_contratacao.id !== this.entidadeForm.get('tipo_contratacao').value.id ||
      +this.entidade.valor_caucao !== +this.entidadeForm.get('valor_caucao').value || +this.entidade.valor_contrato !== +this.entidadeForm.get('valor_contrato').value
      || this.entidade.licitacao !== this.entidadeForm.get('licitacao').value)) {
      this.displayHistorico = true;
      this.cleanScreen = limpa;
    } else {
      this.submitForm(limpa);
    }
  }

  public confirmaHistorico() {
    if (!this.dataContabil) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: 'Informe a data de envio para alterações de contrato ao TCE' });
      return;
    }
    const dtEnvioHistorico: Date = this.dataContabil;
    if (dtEnvioHistorico.getFullYear() !== this.login.exercicio.ano) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: 'O ano da data de envio do histórico de alterações está diferente do exercício logado' });
      return;
    }
    if (!this.podeAlterar(dtEnvioHistorico)) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: 'Data de envio inválida, período na contabilidade já encerrado!' });
      return;
    }
    this.entidadeForm.get('data_historico').setValue(this.dataContabil);
    this.displayHistorico = false;
    this.submitForm(this.cleanScreen);
    this.cleanScreen = false
  }

  protected afterSubmit(ent: Contrato) {
    if (this.currentActionRoute === 'novo' && !this.limparTela) {
      this.entidadeForm.reset();
      this.entidadeForm.get('orgao').setValue(this.login.orgao);
      this.router.navigate(['/contratos', 'novo']);
    }
    this.preencherTabelas();
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  public async onUpload(event: any, fileUpload: any) {
    if (!event.files || event.files.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione o(s) arquivo(s)' });
      return;
    }
    if (this.descricao === null || this.descricao === '') {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'O campo descrição é obrigatório!' });
      return;
    }
    if (this.entidadeForm.valid && this.entidadeForm.get('id').value) {
      try {
        for (const file of event.files) {
          this.uploadedFiles.push(file);
        }
        this.anexoService
          .upload(this.entidade, this.uploadedFiles, this.descricao).pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            this.loadResource();
            this.descricao = null;
            this.router.navigate(['/contratos', res.id, 'editar']);
            this.messageService.add({ severity: 'success', summary: 'Informação', detail: 'Documento enviado com sucesso!' });
          }, (res) => {
            this.messageService.add({ severity: 'error', summary: 'Validação', detail: res.error.payload });
            this.uploadedFiles.length = 0;
          });
      } catch (ex) {
        toastr.error(ex.message ? ex.message : ex);
      }
      this.descricao = undefined;
      fileUpload.clear();
    }
  }

  public download(arquivo: any) {
    this.anexoService
      .download(arquivo.documento).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        const blob = new Blob([res], { type: arquivo.tipo });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = arquivo.nome;
        link.click();
        window.URL.revokeObjectURL(downloadURL);
        $('#dialogUpload').modal('hide');
      });
  }

  public tipoContratacao() {
    if (this.entidadeForm.get('tipo_contratacao').value.servico && this.entidadeForm.get('tipo_contratacao').value.material) {
      console.log('fazer a implementação do tipo de contrato material e serviço');
    }
  }


}
